/**
 * Encrypts a plaintext string with the given DEK and returns a base64 string
 * with the IV prepended to the ciphertext.
 */
export const encryptWithDEK = async ({
  dekBase64,
  plaintext,
}: {
  dekBase64: string;
  plaintext: string;
}) => {
  // Parse the base64 DEK and import it as a CryptoKey
  const dek = await parseDEKFromBase64(dekBase64);

  // Generate a random 12-byte IV — AES-GCM requires a unique IV per encryption
  const iv = crypto.getRandomValues(new Uint8Array(12));

  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    dek,
    new TextEncoder().encode(plaintext),
  );

  // Prepend the IV to the ciphertext so it can be recovered during decryption
  const combined = new Uint8Array([...iv, ...new Uint8Array(encrypted)]);

  return btoa(String.fromCharCode(...combined));
};

/**
 * Decrypts a base64-encoded ciphertext using the given DEK and returns the original plaintext string.
 */
export const decryptWithDEK = async ({
  dekBase64,
  encryptedBase64,
}: {
  dekBase64: string;
  encryptedBase64: string;
}) => {
  // Parse the base64 DEK and import it as a CryptoKey
  const dek = await parseDEKFromBase64(dekBase64);

  // Decode the base64 blob back into bytes (IV + ciphertext)
  const combined = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));

  // Split out the 12-byte IV that was prepended during encryption
  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    dek,
    ciphertext,
  );

  // Decode the decrypted bytes back to the original plaintext string
  return new TextDecoder().decode(decrypted);
};

/**
 * Decodes a base64 DEK string and imports it as a non-extractable AES-GCM CryptoKey.
 */
const parseDEKFromBase64 = (dekBase64: string) => {
  // Decode the base64 string into raw bytes
  const raw = Uint8Array.from(atob(dekBase64), c => c.charCodeAt(0));

  // Import the raw bytes as a non-extractable AES-GCM CryptoKey
  return crypto.subtle.importKey("raw", raw, "AES-GCM", false, [
    "encrypt",
    "decrypt",
  ]);
};
